
import EventItemModal from '@/active-components/event-flow/EventItemModal.vue';
import EventFlowForm from '@/components/event-flow/EventFlowForm.vue';
import EventFlowGrid from '@/components/event-flow/EventFlowGrid.vue';
import CustomModal from '@/components/modal/CustomModal.vue';
import {
    dateWithDifferentYMD,
    getDefaultEndDate,
    getDefaultStartDate,
    getEndOfDay,
    getStartOfDay,
} from '@/lib/date-util';
import ToastMixin from '@/mixins/ToastMixin';
import ToastVariant from '@/model/enums/toast/toast-variant';
import {
    CreateEventFlowItem,
    EventFlowFilterData,
    EventFlowFormData,
    EventFlowItem,
    EventFlowItemRequest,
    ExtraFunctionFormData,
    UpdateEventFlowItem,
} from '@/model/event-flow/EventFlow';
import { addMinutes } from 'date-fns';
import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { mapActions, mapGetters } from 'vuex';
import EventFlowTable from '@/components/event-flow/EventFlowTable.vue';
import cloneDeep from 'lodash.clonedeep';
import {
    EventFlowSettingsResponse,
    EventFlowSettingsUpdate,
    EventResponse,
} from '@/model/Event';
import EventFlowStep from '@/model/enums/event-flow/event-flow-step';
import {
    getEnumKeyByEnumValue,
    getEnumValueByEnumKey,
    setEndDateTime,
    setFlow,
    setStartDateTime,
} from '@/lib/helper/event-flow-helper';
import { Responsible } from '@/model/Responsible';
import { Topic } from '@/model/Topic';
import startOfDay from 'date-fns/startOfDay';
import EventFlowLocationHeader from '@/components/event-flow/EventFlowLocationHeader.vue';
import TopicGrid from '@/components/event-flow/event-flow-grid/TopicGrid.vue';
import GridItem from '@/components/event-flow/event-flow-grid/GridItem.vue';

@Component({
    components: {
        EventFlowForm,
        CustomModal,
        EventItemModal,
        EventFlowTable,
        EventFlowGrid,
        EventFlowLocationHeader,
        TopicGrid,
        GridItem,
    },
    computed: {
        ...mapGetters({
            getEventFlowItems: 'eventFlowStorage/getEventFlowItems',
            getEventFlowDate: 'eventFlowStorage/getEventFlowDate',
            getResponsibles: 'responsibleStorage/getResponsibles',
            getLocations: 'locationsStorage/getLocations',
            getTopics: 'topicsStorage/getTopics',
            getEventFlowSettings: 'eventFlowStorage/getEventFlowSettings',
        }),
    },
    methods: {
        ...mapActions({
            fetchEventFlowItems: 'eventFlowStorage/fetchEventFlowItems',
            fetchResponsibles: 'responsibleStorage/fetchResponsibles',
            fetchLocations: 'locationsStorage/fetchLocations',
            fetchTopics: 'topicsStorage/fetchTopics',
            updateItem: 'eventFlowStorage/updateItem',
            createItem: 'eventFlowStorage/createItem',
            updateEventFlowSettings: 'eventStorage/updateEventFlowSettings',
            fetchEventFlowSettings: 'eventFlowStorage/fetchEventFlowSettings',
        }),
    },
})
export default class EventFlow extends mixins(ToastMixin) {
    @Prop() readonly event!: EventResponse;

    formData: EventFlowFormData = {
        step: null,
        date: undefined,
        startDateTime: undefined,
        endDateTime: undefined,
    };

    filterFormData: EventFlowFilterData = {
        search: '',
        responsibleFilters: [],
        locationFilters: [],
        topicFilters: [],
    };

    extraFunctionFormData = {
        showTime: true,
        showBrief: true,
        showLegend: true,
        showTimeline: true,
    } as ExtraFunctionFormData;

    getEventFlowDate!: Date | null;

    eventFlowSelected = true;

    loading = true;

    selectedItem = {} as EventFlowItem;

    fetchEventFlowItems!: (
        request: EventFlowItemRequest,
    ) => Promise<void | EventFlowItem[]>;

    fetchResponsibles!: (eventId: number) => Promise<void>;

    fetchLocations!: (eventId: number) => Promise<void>;

    fetchTopics!: (eventId: number) => Promise<void>;

    updateItem!: (
        request: UpdateEventFlowItem,
    ) => Promise<void | EventFlowItem>;

    createItem!: (
        request: CreateEventFlowItem,
    ) => Promise<void | EventFlowItem>;

    updateEventFlowSettings!: (
        request: EventFlowSettingsUpdate,
    ) => Promise<void>;

    fetchEventFlowSettings!: (eventId: number) => Promise<void>;

    getEventFlowItems!: EventFlowItem[];

    getResponsibles!: Responsible[];

    getLocations!: Location[];

    getTopics!: Topic[];

    localEventFlowItems = cloneDeep(this.getEventFlowItems ?? []);

    eventFlow = setFlow(cloneDeep(this.localEventFlowItems));

    @Watch('localEventFlowItems')
    eventFlowUpdate(): void {
        this.eventFlow = setFlow(cloneDeep(this.localEventFlowItems));
    }

    getEventFlowSettings!: EventFlowSettingsResponse | null;

    mounted(): void {
        this.fetchEventFlowSettings(this.eventId).then(() => {
            const eventFlowSettings = this.getEventFlowSettings;
            if (
                eventFlowSettings &&
                eventFlowSettings.selectedStartDate &&
                eventFlowSettings.selectedEndDate
            ) {
                this.formData.date = eventFlowSettings.selectedStartDate;

                this.formData.startDateTime = dateWithDifferentYMD(
                    eventFlowSettings.selectedStartDate,
                    this.formData.date,
                );
                this.formData.endDateTime = dateWithDifferentYMD(
                    eventFlowSettings.selectedEndDate,
                    this.formData.date,
                );
                this.formData.step = parseInt(
                    getEnumValueByEnumKey(
                        EventFlowStep,
                        eventFlowSettings.step,
                    ) as string,
                    10,
                );

                this.fetch(
                    this.formData.startDateTime,
                    this.formData.endDateTime,
                );
            }

            this.fetchResponsibles(this.eventId);
            this.fetchLocations(this.eventId);
            this.fetchTopics(this.eventId);
        });
        const box1 = document.querySelector('#location-header');
        const box2 = document.querySelector('#box2');
        if (box1 && box2) {
            box1.addEventListener('scroll', () => {
                box2.scrollLeft = box1.scrollLeft;
            });
            box2.addEventListener('scroll', () => {
                box1.scrollLeft = box2.scrollLeft;
            });
        }
    }

    @Watch('formData.step')
    stepChanged(value: number): void {
        if (this.formData.startDateTime && this.formData.endDateTime) {
            this.updateEventFlowSettings({
                eventId: this.eventId,
                selectedDate: {
                    selectedStartDate: this.formData.startDateTime,
                    selectedEndDate: this.formData.endDateTime,
                    step: getEnumKeyByEnumValue(EventFlowStep, value),
                },
            });
        }
    }

    @Watch('filterFormData', { deep: true })
    onFiltersChanged(): void {
        this.fetch(this.formData.startDateTime, this.formData.endDateTime);
    }

    @Watch('formData.startDateTime')
    onStartDateChanged(value: Date, oldValue: Date): void {
        if (
            oldValue &&
            value.getDate() === oldValue.getDate() &&
            oldValue.toTimeString() !== value.toTimeString()
        ) {
            if (this.formData.endDateTime) {
                this.updateEventFlowSettings({
                    eventId: this.eventId,
                    selectedDate: {
                        selectedStartDate: value,
                        selectedEndDate: this.formData.endDateTime,
                        step: getEnumKeyByEnumValue(
                            EventFlowStep,
                            this.formData.step,
                        ),
                    },
                }).then(() => {
                    if (this.formData.endDateTime) {
                        this.fetch(
                            this.formData.startDateTime,
                            this.formData.endDateTime,
                        );
                    }
                });
            }
        }
    }

    @Watch('formData.endDateTime')
    onEndDateChanged(value: Date, oldValue: Date): void {
        // if the selected endDate is 00:00, then add to the date +1, so it will be next day 00:00.
        if (
            this.formData.date &&
            value.getTime() === startOfDay(value).getTime() &&
            value.getTime() - this.formData.date.getTime() < 1
        ) {
            value.setDate(value.getDate() + 1);
        }

        // if the selected date was 00:00 before, then when you set a differente time, set the day back to the selected date.
        if (
            this.formData.date &&
            value.getDate() > this.formData.date.getDate() &&
            value > getStartOfDay(value)
        ) {
            value.setDate(value.getDate() - 1);
            oldValue.setDate(oldValue.getDate() - 1);
        }

        if (
            oldValue &&
            oldValue.toTimeString() !== value.toTimeString() &&
            (value.getDate() === oldValue.getDate() ||
                value.getTime() === startOfDay(value).getTime())
        ) {
            if (this.formData.startDateTime) {
                this.updateEventFlowSettings({
                    eventId: this.eventId,
                    selectedDate: {
                        selectedStartDate: this.formData.startDateTime,
                        selectedEndDate: value,
                        step: getEnumKeyByEnumValue(
                            EventFlowStep,
                            this.formData.step,
                        ),
                    },
                }).then(() => {
                    this.fetch(
                        this.formData.startDateTime,
                        this.formData.endDateTime,
                    );
                });
            }
        }
    }

    @Watch('formData.date')
    onDateChanged(newDate: Date, oldDate: Date): void {
        if (newDate?.getDate() === oldDate?.getDate()) {
            return;
        }
        if (
            this.formData.date &&
            this.formData.startDateTime &&
            this.formData.endDateTime
        ) {
            this.formData.startDateTime = dateWithDifferentYMD(
                this.formData.startDateTime,
                this.formData.date,
            );
            this.formData.endDateTime = dateWithDifferentYMD(
                this.formData.endDateTime,
                this.formData.date,
            );
        }
        if (
            this.formData.date &&
            this.formData.startDateTime &&
            this.formData.endDateTime &&
            oldDate
        ) {
            this.updateEventFlowSettings({
                eventId: this.eventId,
                selectedDate: {
                    selectedStartDate: this.formData.startDateTime,
                    selectedEndDate: this.formData.endDateTime,
                    step: getEnumKeyByEnumValue(
                        EventFlowStep,
                        this.formData.step,
                    ),
                },
            }).then(() => {
                if (
                    this.formData.date &&
                    this.formData.startDateTime &&
                    this.formData.endDateTime
                ) {
                    this.fetch(
                        setStartDateTime(
                            this.formData.startDateTime,
                            this.formData.date,
                        ),
                        setEndDateTime(
                            this.formData.endDateTime,
                            this.formData.date,
                        ),
                    );
                }
            });
        }
    }

    @Watch('getEventFlowItems', { deep: true })
    onEventFlowItemsChanged(): void {
        this.localEventFlowItems = cloneDeep(this.getEventFlowItems ?? []);
    }

    fetch(startedAt?: Date, endedAt?: Date): Promise<void> {
        return this.fetchEventFlowItems({
            eventId: this.eventId,
            startedAt,
            endedAt,
            ...this.filterFormData,
        })
            .then(() => {
                if (
                    this.formData.date &&
                    this.formData.startDateTime &&
                    this.formData.endDateTime
                ) {
                    this.formData.startDateTime = dateWithDifferentYMD(
                        this.formData.startDateTime,
                        this.formData.date,
                    );
                    this.formData.endDateTime = dateWithDifferentYMD(
                        this.formData.endDateTime,
                        this.formData.date,
                    );
                }
            })
            .finally(() => {
                this.loading = false;
            });
    }

    reloadAfterImport(): Promise<void> {
        return Promise.all([
            this.fetch(this.formData.startDateTime, this.formData.endDateTime),
            this.fetchResponsibles(this.eventId),
            this.fetchLocations(this.eventId),
            this.fetchTopics(this.eventId),
        ]).then(() => {
            // Normalize the combined refresh result to Promise<void>.
        });
    }

    click(item: EventFlowItem): void {
        this.selectedItem = item;
        this.$bvModal.show('event-item-modal');
    }

    success(type: string): void {
        this.$bvModal.hide('event-item-modal');
        this.fetch(this.formData.startDateTime, this.formData.endDateTime);
        this.showToast(
            this.$t(`event-flow.popup.successfully-${type}`).toString(),
            this.$t('event-flow.popup.title').toString(),
            ToastVariant.SUCCESS,
        );
    }

    update(item: EventFlowItem): void {
        if (item) {
            this.updateItem({ eventId: this.eventId, item });
        }
    }

    onCreate(): void {
        if (this.formData.date) {
            const defaultStartDate = this.localEventFlowItems?.length
                ? this.lastEndedAt
                : getDefaultStartDate(this.formData.date);
            const { startDate, endDate } = this.event?.selectedEndDate
                ? this.getClampedEventItemDates(defaultStartDate)
                : {
                      startDate: defaultStartDate,
                      endDate: addMinutes(
                          defaultStartDate,
                          parseInt(this.formData.step as string, 10),
                      ),
                  };

            this.selectedItem = {
                startedAt: startDate,
                endedAt: endDate,
                brief: '',
                description: '',
            };
        }

        this.$bvModal.show('event-item-modal');
    }

    onItemChangedLocal(updatedEventFlowItem: EventFlowItem): void {
        if (
            this.formData.startDateTime &&
            updatedEventFlowItem.endedAt <= this.formData.startDateTime
        ) {
            this.localEventFlowItems = this.localEventFlowItems.filter(
                (item) => item.id !== updatedEventFlowItem.id,
            );
            this.update(updatedEventFlowItem);
            return;
        }

        const oldEventFlowItem = this.localEventFlowItems.find(
            (eventFlowItem) => eventFlowItem.id === updatedEventFlowItem.id,
        );

        const index = this.localEventFlowItems.findIndex(
            (eventFlowItem) => eventFlowItem.id === updatedEventFlowItem.id,
        );

        if (oldEventFlowItem) {
            this.$set(
                this.localEventFlowItems,
                index,
                Object.assign(oldEventFlowItem, updatedEventFlowItem),
            );
        }
    }

    get lastEndedAt(): Date {
        if (!this.localEventFlowItems?.length) {
            if (this.formData.date) {
                return getDefaultEndDate(
                    new Date(this.formData.date.getTime()),
                );
            }

            return getDefaultEndDate(new Date());
        }

        return this.localEventFlowItems
            .map((eventFlowItem) => eventFlowItem.endedAt)
            .reduce((a, b) => {
                return a > b ? a : b;
            });
    }

    getClampedEventItemDates(startDate: Date): {
        startDate: Date;
        endDate: Date;
    } {
        const step = parseInt(this.formData.step as string, 10);
        const eventEndDate = this.getEffectiveItemMaxDate(startDate);
        const proposedStartDate = new Date(startDate);
        let proposedEndDate = addMinutes(proposedStartDate, step);

        if (proposedEndDate <= eventEndDate) {
            return {
                startDate: proposedStartDate,
                endDate: proposedEndDate,
            };
        }

        proposedEndDate = new Date(eventEndDate);

        return {
            startDate: addMinutes(proposedEndDate, -step),
            endDate: proposedEndDate,
        };
    }

    getEffectiveItemMaxDate(startDate: Date): Date {
        const eventEndDate = new Date(this.event.selectedEndDate);
        const endOfSelectedDay = getEndOfDay(new Date(startDate));

        return eventEndDate > endOfSelectedDay
            ? eventEndDate
            : endOfSelectedDay;
    }

    get eventId(): number {
        return parseInt(this.$route.params.id, 10);
    }
}
